import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../services/api.js';
import { geocode } from '../../services/mapbox.js';
import { Button, Input, Textarea } from '../ui/index.js';
import type { GeoFeature, Pin, PinType } from '../../types/index.js';

const PIN_TYPES: { value: PinType; label: string; emoji: string }[] = [
  { value: 'main', label: 'Main', emoji: '📍' },
  { value: 'basecamp', label: 'Base Camp', emoji: '🏠' },
  { value: 'parking', label: 'Parking', emoji: '🅿️' },
  { value: 'hospital', label: 'Hospital', emoji: '🏥' },
];

interface PinFormProps {
  sceneId: string;
  pin?: Pin;
  onClose: () => void;
}

export function PinForm({ sceneId, pin, onClose }: PinFormProps) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(pin?.title ?? '');
  const [address, setAddress] = useState(pin?.address ?? '');
  const [lat, setLat] = useState<number | ''>(pin?.lat ?? '');
  const [lng, setLng] = useState<number | ''>(pin?.lng ?? '');
  const [notes, setNotes] = useState(pin?.notes ?? '');
  const [pinType, setPinType] = useState<PinType>(pin?.pinType ?? 'main');
  const [color] = useState(pin?.color ?? 'blue');
  const [suggestions, setSuggestions] = useState<GeoFeature[]>([]);
  const [searching, setSearching] = useState(false);

  const handleSearch = async () => {
    if (!address.trim()) return;
    setSearching(true);
    const results = await geocode(address);
    setSuggestions(results.slice(0, 5));
    setSearching(false);
  };

  const selectSuggestion = (f: GeoFeature) => {
    setAddress(f.place_name);
    setLng(f.center[0]);
    setLat(f.center[1]);
    setSuggestions([]);
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = { title, address, lat: lat || 0, lng: lng || 0, notes, pinType, color };
      if (pin) return api.put(`/pins/${pin.id}`, payload);
      return api.post('/pins', { sceneId, ...payload });
    },
    onSuccess: (result) => {
      if (!result.success) { toast.error(result.error); return; }
      qc.invalidateQueries({ queryKey: ['pins', sceneId] });
      toast.success(pin ? 'Pin updated' : 'Pin added');
      onClose();
    },
  });

  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
      {/* Pin type selector */}
      <div>
        <label className="text-sm font-medium text-gray-700 block mb-2">Pin type</label>
        <div className="grid grid-cols-4 gap-2">
          {PIN_TYPES.map(t => (
            <button
              key={t.value}
              type="button"
              onClick={() => setPinType(t.value)}
              className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-xs transition-all ${pinType === t.value ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <span className="text-xl">{t.emoji}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      <Input label="Title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Camera position A" required />

      <div className="relative">
        <div className="flex gap-2">
          <div className="flex-1">
            <Input label="Address" value={address} onChange={e => setAddress(e.target.value)} placeholder="Search or type address…" />
          </div>
          <div className="flex items-end">
            <Button type="button" variant="secondary" size="sm" onClick={handleSearch} loading={searching}>Search</Button>
          </div>
        </div>
        {suggestions.length > 0 && (
          <ul className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
            {suggestions.map(f => (
              <li key={f.id} className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm" onClick={() => selectSuggestion(f)}>
                {f.place_name}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Input label="Lat" type="number" step="any" value={lat} onChange={e => setLat(parseFloat(e.target.value) || '')} />
        <Input label="Lng" type="number" step="any" value={lng} onChange={e => setLng(parseFloat(e.target.value) || '')} />
      </div>

      <Textarea label="Notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Access notes, parking info…" />

      <div className="flex gap-3 pt-2">
        <Button type="submit" loading={mutation.isPending} className="flex-1">{pin ? 'Save' : 'Add location'}</Button>
        <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
      </div>
    </form>
  );
}
